import { LabelNodeModel } from "@/models/nodes/LabelNodeModel";
import { SymbolNodeModel } from "@/models/nodes/SymbolNodeModel";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";

import type { CursorModel } from "../CursorModel";
import type { DocumentModel } from "@/models/DocumentModel";

/** Class for elements that will capture cursor on up/down movements */
export const CAPTURE_CURSOR_CLASS_NAME = "capture-cursor";
export const CURSOR_POSITION_CLASS_NAME = "cursor-position";
export const WRAPPER_NODE_CLASS_NAME = "wrapper-node";

const INLINE_NODE_HORIZONTAL_THRESHOLD = 5;

export type MoveToOptions = {
  left: number;
  top: number;
  wrapper?: HTMLElement;
  allowCurrent?: boolean;
  /** Allow only top level inline nodes, only the direct descendants of the wrapper node */
  onlyTopLevel?: boolean;
};

function findInlineNodeElement(
  doc: DocumentModel,
  direction: "up" | "down",
  { left, top, wrapper, allowCurrent, onlyTopLevel }: MoveToOptions,
) {
  const elements = (wrapper ?? document).getElementsByClassName(
    CAPTURE_CURSOR_CLASS_NAME,
  ) as HTMLCollectionOf<HTMLElement>;

  let inlineNodeElement: HTMLElement | null = null;
  let bestBoundary: number | null = null;

  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    const r = el.getBoundingClientRect();

    const matchHorizontalCoords =
      left >= r.left - INLINE_NODE_HORIZONTAL_THRESHOLD &&
      left <= r.right + INLINE_NODE_HORIZONTAL_THRESHOLD;
    const containsCurrentCoords = !allowCurrent && top >= r.top && top <= r.bottom;
    const skipCapture = skipUpDownCapture(doc.cursor, el);

    if (!skipCapture && matchHorizontalCoords && !containsCurrentCoords) {
      if (
        direction === "down" &&
        r.bottom > top &&
        (bestBoundary === null || r.bottom < bestBoundary)
      ) {
        bestBoundary = r.bottom;
        inlineNodeElement = el;
      } else if (
        direction === "up" &&
        r.top < top &&
        (bestBoundary === null || r.top > bestBoundary)
      ) {
        bestBoundary = r.top;
        inlineNodeElement = el;
      }
    }
    if (onlyTopLevel) {
      break;
    }
  }

  return inlineNodeElement;
}

function findCursorElement(
  doc: DocumentModel,
  inlineNodeElement: HTMLElement,
  options: { left: number; top: number },
) {
  const { left } = options;
  const { nodeId } = inlineNodeElement.dataset;

  if (nodeId) {
    const inlineNode = doc.nodes.get(nodeId);
    if (inlineNode) {
      const cursors = inlineNodeElement.getElementsByClassName(
        CURSOR_POSITION_CLASS_NAME,
      ) as HTMLCollectionOf<HTMLElement>;

      let prevCursor: HTMLElement | undefined = undefined;
      let prevCursorLeft = 0;

      for (let i = 0; i < cursors.length; i++) {
        const cursor = cursors[i];
        const { cursorIndex, nodeId: cursorNodeId, cursorPosition } = cursor.dataset || {};
        // Avoid nested cursors
        if (cursorNodeId === nodeId) {
          const cursorRect = cursor.getBoundingClientRect();

          const cursorLeft = cursorPosition === "end" ? cursorRect.right : cursorRect.left;
          if (cursorLeft >= left) {
            // Detect inline editable node and put cursor before it. We will enter edit mode and jump to the
            // correct symbol inside this node.
            if (
              cursorIndex &&
              inlineNode.nodes[parseInt(cursorIndex, 10) - 1]?.isInlineEditable()
            ) {
              return prevCursor;
            }
            const prevIsCloser =
              prevCursor && Math.abs(left - cursorLeft) > Math.abs(left - prevCursorLeft);
            return prevIsCloser ? prevCursor : cursor;
          }
          prevCursorLeft = cursorLeft;
          prevCursor = cursor;
        }
      }
      return prevCursor;
    }
  }
}

/**
 * We skip variable labels when we jump up and down. But if we are already in the label, we allow to jump
 * to the label for the same variable.
 */
function skipUpDownCapture(cursorModel: CursorModel, inlineElement: HTMLElement): boolean {
  const { doc } = cursorModel;
  const { nodeId } = inlineElement.dataset;
  if (nodeId) {
    const node = doc.nodes.get(nodeId);
    if (
      node &&
      node.ancestors[0]?.is(LabelNodeModel) &&
      node.ancestors[1]?.is(SymbolNodeModel) &&
      node.ancestors[2]?.is(VariableNodeModel)
    ) {
      return !cursorModel.node?.ancestors.includes(node.ancestors[2]);
    }
  }
  return false;
}

export function findCursor(
  doc: DocumentModel,
  direction: "up" | "down",
  options: MoveToOptions,
): { nodeId: string; index: number } | undefined {
  const inlineNodeElement = findInlineNodeElement(doc, direction, options);

  if (inlineNodeElement) {
    const cursorElement = findCursorElement(doc, inlineNodeElement, options);

    if (cursorElement) {
      const { nodeId, cursorIndex } = cursorElement.dataset as {
        nodeId?: string;
        cursorIndex?: string;
      };
      const idx = Number(cursorIndex);
      const index = Number.isNaN(idx) ? 0 : idx;

      if (nodeId !== undefined && cursorIndex !== undefined) {
        return { nodeId, index };
      }
    }
  }
}

export function moveUpDown(
  doc: DocumentModel,
  direction: "up" | "down",
  options: MoveToOptions,
): boolean {
  const cursor = findCursor(doc, direction, options);
  if (cursor) {
    const { nodeId, index } = cursor;
    doc.cursor.moveTo(nodeId, index, options);
    return true;
  }
  return false;
}
